import { EventType } from './events.js';
/**
 * Shared classifier for whether a captured Network plugin event is a real,
 * user-facing failure — used by the replay timeline, inspectors, ingest
 * behaviour analysis, error extraction and the summaries.
 *
 * The subtle case is `status === 0`. The browser reports 0 for an *opaque*
 * cross-origin response (a no-cors request such as a GA4 `/g/collect` analytics
 * beacon) and for a request that was blocked or failed outright. A third-party
 * beacon reporting 0 is normal and is NOT the user's friction, so a status-0
 * request only counts as a failure when it is first-party. Real 5xx responses
 * always count; with `clientErrors`, 4xx counts too (the summaries are stricter
 * than the scrubber).
 */
/** The recorded page origin, from the first rrweb Meta event's href. */
export function pageOrigin(events) {
    for (const e of events) {
        if (e.type !== EventType.Meta)
            continue;
        const href = e.data.href;
        if (typeof href === 'string' && href) {
            const o = originOf(href);
            if (o)
                return o;
        }
    }
    return undefined;
}
/** Origin of an absolute URL, or undefined if relative/unparseable. */
export function originOf(url) {
    if (!url)
        return undefined;
    try {
        const u = new URL(url);
        // WHATWG gives every non-special scheme an *opaque* origin — `new
        // URL('app://CartScreen').origin` is literally the string "null". A native
        // session would otherwise have no usable origin at all, so build one from
        // the scheme and host.
        if (u.origin === 'null')
            return `${u.protocol}//${u.host}`;
        return u.origin;
    }
    catch {
        return undefined;
    }
}
/** True when `url` targets a different origin than the page. Relative → first-party. */
export function isThirdParty(url, origin) {
    if (!url || !/^https?:\/\//i.test(url))
        return false;
    // Native sessions carry an app:// origin. "Third party" is a browser concept —
    // it exists because a cross-origin no-cors beacon legitimately reports status
    // 0. An app has no same-origin policy, so a status-0 request there means the
    // request actually failed, and treating every host as third-party would hide
    // exactly the failures worth seeing.
    if (origin?.startsWith('app:'))
        return false;
    try {
        return new URL(url).origin !== origin;
    }
    catch {
        return false;
    }
}
/** Whether a captured Network request should count as a real failure/friction. */
export function isNetworkFailure(status, url, origin, clientErrors = false) {
    if (status >= 500)
        return true;
    if (clientErrors && status >= 400)
        return true;
    if (status === 0)
        return !isThirdParty(url, origin);
    return false;
}
/**
 * Whether a captured Error event is real, visitor-facing friction.
 *
 * The browser's capture-phase `error` listener also fires for failed RESOURCE
 * loads (an ad-blocked analytics beacon, a dead third-party widget). Those
 * arrive with no message at all, and blaming the visitor's experience for a
 * blocked third-party asset inflates the frustration score. So:
 *  - a resource failure counts only when the asset is first-party;
 *  - an event carrying neither a message nor a stack is noise and never counts.
 */
export function isCountableError(payload, origin) {
    const p = payload ?? {};
    if (p.kind === 'resource')
        return !isThirdParty(p.source, origin);
    if (!p.message && !p.stack)
        return false;
    return true;
}
//# sourceMappingURL=networkFailure.js.map